/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import Component from 'can/component/component';
import template from './live-video-slider.stache';
import can from 'can';
import DomEventsManager from 'scripts/dom-events-manager';
import Swiper from 'swiper';
import FeedService from 'scripts/services/base-feed-service';
import Map from 'scripts/models/base/map';
import _partition from 'lodash/partition';
import _differenceBy from 'lodash/differenceBy';
import dtm from 'scripts/services/dtm';

const contentFeedUrl = window.globalSettings.feeds.contentFeed;
const SLIDES_NUMBER = 20;

const Model = can.Map.extend({
    initLoad: true,
    define: {
        slides: {
            Type: Map.List,
            Value: '*',
        },
        unseenCards: {
            type: 'number',
            value: 0,
        },
    },
});

Component.extend({
    tag: 'video-slider',
    template: template,
    leakScope: false,
    viewModel: Model,
    events: {
        inserted: function() {
            DomEventsManager.subscribe(this);
            this.dataContainer = this.element.find(
                '.s-video-landing-slider__container',
            );
            this.contentFeedService = new FeedService({
                feedUrl: contentFeedUrl,
                fromCache: false,
                autoUpdate: false,
            })
                .on('data', (ev, data) => {
                    if (data && data.length > 0) {
                        data.sort((d1, d2) => {
                            return d2.date - d1.date;
                        });

                        if (this.viewModel.attr('initLoad')) {
                            this.viewModel.attr('initLoad', false);
                            this.data = [...data];

                            let dataPartitions = _partition(data, (d) => {
                                return d.hasOwnProperty('pinnedPosition');
                            });
                            let pinnedCards = dataPartitions[0].sort(
                                (pc1, pc2) => {
                                    return (
                                        pc1.pinnedPosition - pc2.pinnedPosition
                                    );
                                },
                            );
                            let unpinnedData = dataPartitions[1];

                            let sortedData = new Array(data.length).fill(
                                undefined,
                            );

                            sortedData = sortedData.map((v, i) => {
                                if (
                                    pinnedCards[0] &&
                                    i === pinnedCards[0].pinnedPosition
                                ) {
                                    return pinnedCards.shift();
                                } else {
                                    return unpinnedData.shift();
                                }
                            });

                            let initSlidesData = sortedData.slice(
                                0,
                                SLIDES_NUMBER,
                            );
                            this.oldSlidesData = sortedData.slice(
                                SLIDES_NUMBER,
                                data.length,
                            );

                            this.viewModel.attr('slides', initSlidesData);
                            this.initSwiper();
                        } else {
                            let removedCards = _differenceBy(
                                this.data,
                                data,
                                'id',
                            );
                            if (removedCards.length > 0) {
                                this.removeCards(
                                    removedCards,
                                    this.viewModel.slides,
                                    true,
                                );
                                this.removeCards(
                                    removedCards,
                                    this.oldSlidesData,
                                    false,
                                );
                            }

                            let newEventsData = _differenceBy(
                                data,
                                this.data,
                                'id',
                            );
                            if (newEventsData.length > 0) {
                                this.addCardsToSlider(newEventsData, data);
                            }
                        }
                    }
                })
                .on('error', (error) => {
                    console.error('Error fetching content feed data', error);
                });

            this.contentFeedService.fetch();
            setInterval(() => {
                this.contentFeedService.fetch();
            }, 60000);
        },

        removed: function() {
            DomEventsManager.unsubscribe(this);
        },

        hideFocusHiddenSlides(swiperContainer) {
            const swiperSlide = '.s-video-landing-slider-item';
            swiperContainer.find(swiperSlide).attr('tabindex', '-1');
            swiperContainer
                .find(swiperSlide)
                .find('a')
                .attr('tabindex', '-1');
            swiperContainer
                .find(swiperSlide)
                .find('button')
                .attr('tabindex', '-1');
            swiperContainer.find('.swiper-slide-visible').attr('tabindex', '0');
            swiperContainer
                .find('.swiper-slide-visible')
                .find('a')
                .attr('tabindex', '0');
            swiperContainer
                .find('.swiper-slide-visible')
                .find('button')
                .attr('tabindex', '0');
        },

        initSwiper() {
            const swiperContainer = this.element.find(
                '.video-landing-swiper-container',
            );
            this.swiper = new Swiper(swiperContainer, {
                centeredSlides: false,
                spaceBetween: 30,
                wrapperClass: 's-video-landing-slider__container',
                slidesPerView: 'auto',
                slideClass: 's-video-landing-slider-item',
                watchSlidesVisibility: true,
                navigation: {
                    nextEl: swiperContainer.find(
                        '.s-video-landing-slider-button--next',
                    ),
                    prevEl: swiperContainer.find(
                        '.s-video-landing-slider-button--prev',
                    ),
                },
                on: {
                    init: () =>
                        setTimeout(
                            () => this.hideFocusHiddenSlides(swiperContainer),
                            2000,
                        ),
                    slideChangeTransitionStart: () => this.appendOldSlides(),
                    slideChangeTransitionEnd: () =>
                        this.hideFocusHiddenSlides(swiperContainer),
                    reachBeginning: () => this.viewModel.attr('unseenCards', 0),
                },
            });

            this.viewModel.attr('unseenCards', 0);
        },

        addCardsToSlider(newEventsData, responseData) {
            this.viewModel.slides.unshift(...newEventsData);

            this.data = [...responseData];

            this.swiper.update();
            if (!this.swiper.isBeginning) {
                this.swiper.slideTo(
                    newEventsData.length + this.swiper.activeIndex,
                    0,
                );
                this.viewModel.attr(
                    'unseenCards',
                    this.viewModel.attr('unseenCards') + newEventsData.length,
                );
            } else {
                this.swiper.slideTo(1, 0);
                this.swiper.slidePrev();
            }
        },

        removeCards(removedCards, slides, removeInSwiper) {
            removedCards.forEach((removed) => {
                let index = _.findIndex(slides, { id: removed.id });

                if (index >= 0) {
                    if (removeInSwiper) {
                        this.swiper.removeSlide(index);
                    }
                    slides.splice(index, 1);
                }
            });
        },

        appendOldSlides() {
            let invisibleToRight = this.element
                .find('.s-video-landing-slider-item')
                .filter((index, slide) => {
                    return $(slide).position().left > $(window).width();
                });
            if (invisibleToRight.length <= 2 && this.oldSlidesData.length > 0) {
                this.viewModel.slides.push(
                    ...this.oldSlidesData.splice(0, SLIDES_NUMBER),
                );
                this.swiper.update();
            }
        },

        '.s-video-landing-slider-item--new-events click'(element, event) {
            this.swiper.slideTo(0, 300);
        },

        '.s-video-landing-slider-item click'(element) {
            const cardIndex = $(element).index();
            const cards = this.viewModel.attr('slides');

            if (cards.length > cardIndex) {
                const selectedCard = cards[cardIndex];
                dtm.trackMixedCard(selectedCard.type);
            }
        },
    },
});
